import { useState, ChangeEvent, FC, useEffect } from 'react';
import { css } from '@emotion/css';
import { Typography, Dialog } from '@prezzee/components';
import { spacing, colors } from '@prezzee/tokens';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import RetailerList from './RetailerList';
import SearchBar from './SearchBar';

import { Sku } from 'models/sku.model';

const style = {
  dialog: css`
    [role='dialog'] {
      max-width: 800px;

      width: 100%;
      height: 100%;

      @media (min-height: 864px) {
        max-height: 800px;
      }
    }
  `,
  retailerContainer: css`
    padding: ${spacing.spacing06};
    padding-top: 0;
    display: flex;
    flex-direction: column;
  `,
  stickySearchHeader: css`
    position: -webkit-sticky;
    position: sticky;
    top: 0;
    background-color: white;
    padding-top: ${spacing.spacing06};
    padding-bottom: ${spacing.spacing04};
    z-index: 9;
  `,
  close: css`
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background-color: ${colors.lilac50};
    position: absolute;
    right: 0;
    top: ${spacing.spacing06};
  `,
  title: css`
    margin-bottom: ${spacing.spacing05} !important;
    padding-right: 35px;
  `,
};

interface Props {
  retailerList: Sku[] | null;
  isOpen: boolean;
  handleRetailerListClose: () => void;
}

const RetailerListDialog: FC<Props> = ({ retailerList, isOpen, handleRetailerListClose }) => {
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!isOpen && searchTerm) {
      setSearchTerm('');
    }
  }, [isOpen, searchTerm]);

  const handleSearchChange = (e: ChangeEvent) => {
    const target = e.currentTarget as HTMLInputElement;
    setSearchTerm(target.value.toLocaleLowerCase().replace(/[^\w\s]/g, '\\$&'));
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleRetailerListClose}
      className={style.dialog}
      aria-labelledby="retailer-list-dialog-title"
      aria-describedby="retailer-list-dialog-description"
    >
      <div className={style.retailerContainer}>
        <div className={style.stickySearchHeader}>
          <button
            onClick={handleRetailerListClose}
            className={style.close}
            title="Close retailer list dialog"
            aria-label="Close retailer list dialog"
          >
            <FontAwesomeIcon icon={['fas', 'times']} />
          </button>
          <Typography variant="h4" component="h3" id="retailer-list-dialog-title" className={style.title}>
            Swap for these retailers
          </Typography>
          {retailerList.length >= 16 && <SearchBar placeholder="Search brands" onChange={handleSearchChange} />}
        </div>
        <RetailerList retailers={retailerList} searchTerm={searchTerm} />
      </div>
    </Dialog>
  );
};

export default RetailerListDialog;
